<template>
  <div class="summary-mode-sidebar" data-testid="summary-mode-sidebar">
    <div class="summary-mode-sidebar__video">
      <ViewerContent />
    </div>

    <ItemName class="summary-mode-sidebar__name" />

    <div class="summary-mode-sidebar__meta">
      <ButtonViews />
      <ButtonExpires />
    </div>

    <div v-if="!reactionsDisabled" class="summary-mode-sidebar__reactions">
      <Reactions :id="id" />
    </div>

    <div class="summary-mode-sidebar__actions">
      <SmartActionsList v-if="hasAiAddon" />
      <SmartActionsUpsell v-else />
    </div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';

import ViewerContent from '@/modules/viewer/components/Content.vue';
import ItemName from '@/modules/viewer/components/Name.vue';
import Reactions from '@/modules/viewer/components/Reactions.vue';
import ButtonViews from '@/modules/viewer/components/meta_buttons/ButtonViews.vue';
import ButtonExpires from '@/modules/viewer/components/meta_buttons/ButtonExpires.vue';
import SmartActionsList from '@/modules/viewer/components/SmartActionsList.vue';
import SmartActionsUpsell from '@/modules/viewer/components/SmartActionsUpsell.vue';

import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useVideoAi } from '@/modules/viewer/composables/useVideoAi';
import { useReactions } from '@/modules/viewer/composables/useReactions';

const { id } = storeToRefs(useViewerItemStore());
const { hasAiAddon } = useVideoAi();
const { reactionsDisabled } = useReactions();
</script>

<style scoped>
.summary-mode-sidebar {
  @apply flex flex-col w-full gap-3;
}

/* Content.vue's root already applies the global `.content-wrapper` class
   (storybook/theme/theme.css), which sets its own aspect-ratio — including
   viewport-height-band overrides (16/6, 16/7) tuned for the full-page video
   viewer. Forcing a second, fixed aspect-video here fought that: on the
   bands where content-wrapper shrinks below 16/9, this box stayed taller,
   leaving blank space below the video. Let content-wrapper own the sizing. */
.summary-mode-sidebar__video {
  @apply w-full flex-shrink-0 rounded-10 overflow-hidden;
}

.summary-mode-sidebar__video :deep(.content-wrapper) {
  background-color: transparent !important;
}

.summary-mode-sidebar__name {
  @apply text-base font-bold;
}

.summary-mode-sidebar__meta {
  @apply flex flex-wrap gap-x-3 gap-y-1 text-xs sm:text-sm text-zinc-600 dark:text-zinc-300;
}

.summary-mode-sidebar__reactions {
  @apply flex flex-wrap gap-4 pb-3 border-b border-slate-200 dark:border-zinc-700;
}

/* Reactions.vue's default button style (bordered, large) is tuned for the
   Comments & Reactions tab — restyle to the compact filled pill this
   sidebar calls for, without changing the shared component's default. */
.summary-mode-sidebar__reactions :deep(.reactions-button) {
  @apply gap-0.5 px-2 py-2 h-auto border-none rounded-lg bg-slate-100 dark:bg-zinc-700;
}

/* Keep the selected reaction's blue state above this sidebar's compact
   default pill background, so its white count has sufficient contrast. */
.summary-mode-sidebar__reactions :deep(.reactions-button.bg-blue-500) {
  background-color: rgb(59 130 246);
}

.summary-mode-sidebar__reactions :deep(.reactions-button.bg-blue-600) {
  background-color: rgb(37 99 235);
}

.summary-mode-sidebar__reactions :deep(.reactions-emoji) {
  @apply text-base;
}

.summary-mode-sidebar__reactions :deep(.reactions-counter) {
  @apply text-xs;
}

.summary-mode-sidebar__actions {
  @apply rounded-10 border border-slate-200 dark:border-zinc-700;
}
</style>
